import {
  isOpenAnalystHostedBrowserAuthBaseUrl,
  openAnalystBrowserAuthBaseUrl,
  openAnalystTenxBaseUrl,
} from "@/utils/openanalyst-hosts"
import { localDevAuthBypass } from "./dev-bypass"

let accessToken: string | null = null
let openAnalystAccessToken: string | null = null
let controlPlaneAccessToken: string | null = null
let openAnalystAccountAccessToken: string | null = null
let refreshPromise: Promise<string | null> | null = null
let webRefreshFetch: typeof fetch | null = null
let lastRefreshFailure: unknown = null
const WEB_REFRESH_TIMEOUT_MS = 30_000
const DESKTOP_REFRESH_REJECTED =
  /OpenAnalyst desktop session rejected \((?:401|403)\)|OpenAnalyst API session missing\. Please sign in again\./i

const desktopRefreshWasRejected = (error: unknown) =>
  error instanceof Error && DESKTOP_REFRESH_REJECTED.test(error.message)

export type AccessTokenSource = "openanalyst" | "tenx_control_plane" | "openanalyst_account"
export type OpenAnalystAccountModel = { providerID: string; modelID: string; variant?: string }

type DesktopRefreshConfig = {
  refresh: () => Promise<AuthTokenPayload | null>
  clearTokens: () => Promise<void>
}

type AuthTokenPayload = {
  accessToken?: string | null
  access_token?: string | null
  refreshToken?: string | null
  refresh_token?: string | null
  openAnalyst?: AuthTokenPayload | null
  controlPlane?: AuthTokenPayload | null
}

let desktopRefresh: DesktopRefreshConfig | null = null

const modelClaimKeys = [
  "openAnalystModel",
  "openanalystModel",
  "openanalyst_model",
  "oaModel",
  "oa_model",
  "defaultModel",
  "default_model",
  "selectedModel",
  "selected_model",
  "model",
  "modelID",
  "modelId",
  "model_id",
]
const modelClaimContainers = ["openAnalyst", "openanalyst", "account", "subscription", "entitlements"]

function cleanString(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : undefined
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined
}

function field(input: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(input, key)) return input[key]
  }
}

function modelFromParts(input: {
  providerID?: unknown
  modelID?: unknown
  variant?: unknown
}): OpenAnalystAccountModel | undefined {
  const modelID = cleanString(input.modelID)
  if (!modelID) return undefined
  const providerID = cleanString(input.providerID) ?? "openanalyst"
  if (providerID !== "openanalyst") return undefined
  return {
    providerID,
    modelID,
    ...(cleanString(input.variant) ? { variant: cleanString(input.variant) } : {}),
  }
}

function modelFromString(value: unknown): OpenAnalystAccountModel | undefined {
  const raw = cleanString(value)
  if (!raw) return undefined
  const slash = raw.indexOf("/")
  if (slash === -1) return { providerID: "openanalyst", modelID: raw }
  const providerID = raw.slice(0, slash).trim()
  const modelID = raw.slice(slash + 1).trim()
  if (!providerID || !modelID) return undefined
  if (providerID !== "openanalyst") return undefined
  return { providerID, modelID }
}

export function parseOpenAnalystAccountModelClaim(value: unknown): OpenAnalystAccountModel | undefined {
  const fromString = modelFromString(value)
  if (fromString) return fromString

  const item = record(value)
  if (!item) return undefined

  return modelFromParts({
    providerID: field(item, ["providerID", "providerId", "provider_id", "provider"]),
    modelID: field(item, ["modelID", "modelId", "model_id", "id", "model"]),
    variant: field(item, ["variant", "reasoning", "mode"]),
  })
}

function decodeJwtClaims(token: string | null): Record<string, unknown> | undefined {
  const part = token?.split(".")[1]
  if (!part || typeof atob !== "function") return undefined
  try {
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/")
    return record(JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, "="))))
  } catch {
    return undefined
  }
}

export function openAnalystAccountModelFromClaims(claims: unknown): OpenAnalystAccountModel | undefined {
  const data = record(claims)
  if (!data) return undefined

  const direct = parseOpenAnalystAccountModelClaim(field(data, modelClaimKeys))
  if (direct) return direct

  for (const key of modelClaimContainers) {
    const nested = record(data[key])
    if (!nested) continue
    const model = parseOpenAnalystAccountModelClaim(field(nested, modelClaimKeys))
    if (model) return model
  }
}

export function getOpenAnalystAccountModelFromToken(token = openAnalystAccessToken): OpenAnalystAccountModel | undefined {
  return openAnalystAccountModelFromClaims(decodeJwtClaims(token))
}

function tokenExpirySeconds(token: string | null) {
  const exp = decodeJwtClaims(token)?.exp
  if (typeof exp === "number" && Number.isFinite(exp)) return exp
  if (typeof exp !== "string" || !exp.trim()) return null
  const parsed = Number(exp)
  return Number.isFinite(parsed) ? parsed : null
}

function validCachedOpenAnalystAccessToken() {
  const exp = tokenExpirySeconds(openAnalystAccessToken)
  if (!openAnalystAccessToken || !exp) return null
  return exp > Math.floor(Date.now() / 1000) + 30 ? openAnalystAccessToken : null
}

// How early (seconds before expiry) we proactively refresh instead of reusing a token.
const REFRESH_EXPIRY_SKEW_SECONDS = 60
// A control-plane token with no readable expiry (opaque) is still refreshed at most once per this
// window — a reconnect/retry loop must never storm the refresh endpoint (the ~1/sec auth storm).
const CONTROL_PLANE_REFRESH_MIN_INTERVAL_MS = 60_000
let lastControlPlaneRefreshAt = 0

function validCachedControlPlaneAccessToken() {
  const exp = tokenExpirySeconds(controlPlaneAccessToken)
  if (!controlPlaneAccessToken || !exp) return null
  return exp > Math.floor(Date.now() / 1000) + REFRESH_EXPIRY_SKEW_SECONDS ? controlPlaneAccessToken : null
}

function tokenPayload(value: AuthTokenPayload | null | undefined) {
  return value && typeof value === "object" ? value : null
}

function hasOpenAnalystSlot(value: AuthTokenPayload | null | undefined) {
  return !!value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "openAnalyst")
}

function hasControlPlaneSlot(value: AuthTokenPayload | null | undefined) {
  return !!value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "controlPlane")
}

function payloadAccessToken(payload: AuthTokenPayload | null | undefined) {
  if (!payload) return null
  const value = typeof payload.accessToken === "string" ? payload.accessToken : payload.access_token
  return typeof value === "string" && value.trim() ? value.trim() : null
}

async function authTokenPayload(response: Response) {
  const text = await response.text()
  if (!text) return null
  try {
    return JSON.parse(text) as AuthTokenPayload
  } catch {
    return null
  }
}

function applyOpenAnalystRefreshedTokens(payload: AuthTokenPayload | null | undefined) {
  const data = tokenPayload(payload)
  if (!data) return null
  const openAnalystToken =
    payloadAccessToken(tokenPayload(data.openAnalyst)) ??
    payloadAccessToken(data)
  if (openAnalystToken) setAccessToken(openAnalystToken, "openanalyst")
  else if (hasOpenAnalystSlot(data)) clearOpenAnalystAccessToken()
  const controlPlaneToken = payloadAccessToken(tokenPayload(data.controlPlane))
  if (controlPlaneToken) setAccessToken(controlPlaneToken, "tenx_control_plane")
  return openAnalystToken
}

function applyHostedBrowserRefreshedTokens(payload: AuthTokenPayload | null | undefined) {
  const data = tokenPayload(payload)
  if (!data) return null
  const topLevelToken = payloadAccessToken(data)
  const routeToken = payloadAccessToken(tokenPayload(data.controlPlane)) ?? topLevelToken
  if (!routeToken) return null
  if (topLevelToken) setAccessToken(topLevelToken, "openanalyst_account")
  const claims = decodeJwtClaims(topLevelToken)
  const topLevelLooksOpenAnalyst =
    cleanString(field(claims ?? {}, ["oaBindingTenantId", "oa_binding_tenant_id"])) ||
    (cleanString(field(claims ?? {}, ["userId", "user_id"])) &&
      cleanString(field(claims ?? {}, ["workspaceId", "workspace_id"])))
  const topLevelOpenAnalystToken =
    !hasOpenAnalystSlot(data) && (hasControlPlaneSlot(data) || (!openAnalystAccessToken && topLevelLooksOpenAnalyst))
      ? topLevelToken
      : null
  const openAnalystToken = payloadAccessToken(tokenPayload(data.openAnalyst)) ?? topLevelOpenAnalystToken
  if (openAnalystToken) setAccessToken(openAnalystToken, "openanalyst")
  else if (hasOpenAnalystSlot(data)) clearOpenAnalystAccessToken()
  setAccessToken(routeToken, "tenx_control_plane")
  return routeToken
}

function applyRefreshedTokens(payload: AuthTokenPayload | null | undefined) {
  const data = tokenPayload(payload)
  if (!data) return null
  const controlPlane = tokenPayload(data.controlPlane)
  const topLevelToken = payloadAccessToken(data)
  if (topLevelToken) setAccessToken(topLevelToken, "openanalyst_account")
  const openAnalystToken =
    payloadAccessToken(tokenPayload(data.openAnalyst)) ??
    (controlPlane || !hasOpenAnalystSlot(data) ? topLevelToken : null)
  if (openAnalystToken) setAccessToken(openAnalystToken, "openanalyst")
  else if (hasOpenAnalystSlot(data)) clearOpenAnalystAccessToken()
  const controlPlaneToken = payloadAccessToken(controlPlane ?? data)
  if (!controlPlaneToken) return null
  setAccessToken(controlPlaneToken, "tenx_control_plane")
  return controlPlaneToken
}

export function getAccessToken(): string | null {
  return accessToken
}

export function getOpenAnalystAccessToken(): string | null {
  return openAnalystAccessToken
}

export function clearOpenAnalystAccessToken(): void {
  if (openAnalystAccessToken && accessToken === openAnalystAccessToken) {
    accessToken = controlPlaneAccessToken ?? openAnalystAccountAccessToken
  }
  openAnalystAccessToken = null
}

export function getControlPlaneAccessToken(): string | null {
  return controlPlaneAccessToken
}

export function getDesktopSidecarAccessToken(): string | null {
  return controlPlaneAccessToken ?? openAnalystAccountAccessToken ?? openAnalystAccessToken ?? accessToken
}

export function getOpenAnalystAccountAccessToken(): string | null {
  return openAnalystAccountAccessToken ?? controlPlaneAccessToken ?? openAnalystAccessToken ?? accessToken
}

// Requests carry the cloud token in a second header alongside the sidecar's HTTP Basic, so a
// tokenless request still looks authorized to the fetch layer and goes out anyway — the engine
// then rejects it with `auth_header_missing`. Callers that load data need to know whether a token
// is actually available before firing, and need to hear about it when one arrives: logging out and
// back in leaves the app mounted for as long as it takes to type an email and an OTP, and every
// load fired in that window fails. `authTokenReady` answers the first question and
// `onAuthTokenChange` the second.
const authTokenListeners = new Set<() => void>()
let tokenEverSet = false

export function authTokenReady(): boolean {
  if (accessToken) return true
  // A dev-bypass build has no token by design and must not wait for one.
  if (localDevAuthBypass()) return true
  // Nothing has ever set a token in this process, so there is no way to tell "not yet" from "not
  // needed" — a sidecar started with OPENANALYST_DISABLE_AUTH legitimately never gets one. Fail
  // open: holding loads back forever would leave the app silently empty, which is a worse failure
  // than the doomed requests this is here to prevent. Once one token has been seen we know tokens
  // are in play, and a later absence really does mean "not yet".
  return !tokenEverSet
}

export function onAuthTokenChange(listener: () => void): () => void {
  authTokenListeners.add(listener)
  return () => {
    authTokenListeners.delete(listener)
  }
}

// Only fires when readiness actually flips, which collapses the two or three back-to-back
// setAccessToken calls of a single sign-in into one notification.
function notifyAuthTokenReadyChange(before: boolean): void {
  if (authTokenReady() === before) return
  for (const listener of [...authTokenListeners]) {
    try {
      listener()
    } catch {
      // A listener that throws must not leave the token half-applied.
    }
  }
}

export function setAccessToken(token: string | null, source: AccessTokenSource = "openanalyst"): void {
  const ready = authTokenReady()
  if (token) tokenEverSet = true
  accessToken = token
  if (source === "tenx_control_plane") controlPlaneAccessToken = token
  else if (source === "openanalyst_account") openAnalystAccountAccessToken = token
  else openAnalystAccessToken = token
  notifyAuthTokenReadyChange(ready)
}

export function clearAccessToken(): void {
  const ready = authTokenReady()
  accessToken = null
  openAnalystAccessToken = null
  controlPlaneAccessToken = null
  openAnalystAccountAccessToken = null
  refreshPromise = null
  lastControlPlaneRefreshAt = 0
  notifyAuthTokenReadyChange(ready)
}

export function configureDesktopRefresh(config: DesktopRefreshConfig): void {
  desktopRefresh = config
}

export function resetDesktopRefresh(): void {
  desktopRefresh = null
}

export function configureWebRefreshFetch(fetchImpl: typeof fetch | null): void {
  webRefreshFetch = fetchImpl
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      lastRefreshFailure = null
      if (desktopRefresh) return await refreshDesktop()
      return await refreshWeb()
    } catch (error) {
      lastRefreshFailure = error
      clearAccessToken()
      if (desktopRefreshWasRejected(error)) throw error
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export function consumeLastRefreshFailure(): unknown {
  const failure = lastRefreshFailure
  lastRefreshFailure = null
  return failure
}

export async function refreshControlPlaneAccessToken(): Promise<string | null> {
  // Reuse a still-valid control-plane token instead of refreshing on every call. Tokens live ~24h,
  // and a reconnect/retry loop (e.g. a stale project's MCP command.list) must not trigger a network
  // refresh each time — that was the ~1/sec auth-refresh storm. Only go to the network near expiry;
  // when the token is opaque (no readable expiry) still throttle to at most one refresh per minute.
  const valid = validCachedControlPlaneAccessToken()
  if (valid) return valid
  if (controlPlaneAccessToken && Date.now() - lastControlPlaneRefreshAt < CONTROL_PLANE_REFRESH_MIN_INTERVAL_MS) {
    return controlPlaneAccessToken
  }
  await refreshAccessToken()
  lastControlPlaneRefreshAt = Date.now()
  return controlPlaneAccessToken
}

export async function refreshOpenAnalystAccessToken(): Promise<string | null> {
  // Same reuse rule for the OpenAnalyst provider token (LLM + credits): a valid cached token is
  // reused; an opaque token (no readable expiry) is reused rather than refreshed on every call; only
  // a missing or near-expiry token goes to the network.
  const valid = validCachedOpenAnalystAccessToken()
  if (valid) return valid
  if (openAnalystAccessToken && tokenExpirySeconds(openAnalystAccessToken) === null) return openAnalystAccessToken
  await refreshAccessToken()
  return openAnalystAccessToken
}

function webRefreshSignal() {
  return (AbortSignal as unknown as { timeout?: (ms: number) => AbortSignal }).timeout?.call(
    AbortSignal,
    WEB_REFRESH_TIMEOUT_MS,
  )
}

async function refreshWeb(): Promise<string | null> {
  if (localDevAuthBypass()) return null

  const authBaseUrl = openAnalystBrowserAuthBaseUrl()
  const hostedBrowserAuth = isOpenAnalystHostedBrowserAuthBaseUrl(authBaseUrl)
  const response = await (webRefreshFetch ?? fetch)(`${authBaseUrl}/api/v1/userAccount/refresh/token`, {
    method: "POST",
    credentials: "include",
    signal: webRefreshSignal(),
    headers: {
      Accept: "application/json",
    },
  })

  if (response.ok) {
    const payload = await authTokenPayload(response)
    const nextAccessToken = hostedBrowserAuth
      ? applyHostedBrowserRefreshedTokens(payload)
      : applyOpenAnalystRefreshedTokens(payload)
    if (nextAccessToken) return accessToken
  }

  if (hostedBrowserAuth) {
    const cachedOpenAnalystToken = validCachedOpenAnalystAccessToken()
    if (cachedOpenAnalystToken) return cachedOpenAnalystToken
    clearAccessToken()
    return null
  }

  const tenxResponse = await (webRefreshFetch ?? fetch)(`${openAnalystTenxBaseUrl()}/v2/auth/refresh`, {
    method: "POST",
    credentials: "include",
  })

  if (!tenxResponse.ok) {
    clearAccessToken()
    return null
  }

  const nextAccessToken = applyRefreshedTokens(await authTokenPayload(tenxResponse))
  if (!nextAccessToken) {
    clearAccessToken()
    return null
  }

  return accessToken
}

async function refreshDesktop(): Promise<string | null> {
  if (!desktopRefresh) return null

  // The desktop refresh IPC (openAnalystAuthRefresh → control-plane/Smartwork exchange)
  // had NO timeout, unlike refreshWeb (which uses AbortSignal.timeout). A hung exchange
  // would stall every caller forever (a prime onboarding "creating handle" freeze).
  // Bound it; on timeout degrade transiently — return null WITHOUT clearing cached
  // tokens, so a retry can still use them (a stalled exchange must not log the user out).
  let refreshed: Awaited<ReturnType<NonNullable<typeof desktopRefresh>["refresh"]>>
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  try {
    refreshed = await Promise.race([
      desktopRefresh.refresh(),
      // clearTimeout in finally so the loser of the race never leaks a pending timer
      // (and never fires an unhandled rejection after refresh() already won).
      new Promise<never>((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error("desktop token refresh timed out")), WEB_REFRESH_TIMEOUT_MS)
      }),
    ])
  } catch (error) {
    lastRefreshFailure = error
    if (desktopRefreshWasRejected(error)) throw error
    return null
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }

  const nextAccessToken = applyRefreshedTokens(refreshed)

  if (!nextAccessToken) {
    /**
     * No token came back — so forget what we hold, but do NOT delete the session on disk.
     *
     * `desktopRefresh.clearTokens()` is not a local reset: it reaches into the main process and
     * wipes the stored session, refresh cookie included. This called it whenever the exchange
     * produced no token, and `openAnalystAuthRefresh` returns null for "I could not do that right
     * now" every bit as much as for "you are signed out" — no stored session yet, a store it did
     * not recognise, a control-plane refresh that came back empty. So a main-process shrug became
     * a renderer decision to destroy the login, permanently: the refresh token that would have
     * fixed it on the next try went with it.
     *
     * The renderer is the wrong place to make that call, and it was never needed here: when the
     * main process has actual evidence the session is invalid — a 401 or 403 — it clears the store
     * ITSELF (shouldClearDesktopAuthAfterRefreshFailure, clearDesktopAuthSession). This branch
     * only ever added deletions the main process had deliberately declined to make.
     *
     * The timeout path above already reasons this way ("a stalled exchange must not log the user
     * out"); an empty result is the same class of non-evidence and gets the same treatment now.
     * Clearing the in-memory token is still right — we have nothing usable — and it makes the next
     * call refresh again rather than assume.
     */
    clearAccessToken()
    return null
  }

  setAccessToken(nextAccessToken, "tenx_control_plane")
  return accessToken
}
